import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown, FileText, ListChecks, Scale, Shield, ShieldCheck } from "lucide-react";
import { MemoProofSection } from "@/components/MemoProofSection";
import { ChecklistLeadBand } from "@/components/ChecklistLeadBand";
import { AuthorityStrip } from "@/components/AuthorityStrip";
import { ForbesHeroLine } from "@/components/ForbesHeroLine";
import { LadderAtAGlance } from "@/components/LadderAtAGlance";
import { NextSessionStrip } from "@/components/NextSessionStrip";
import { ReviewHighlights } from "@/components/ReviewHighlights";
import { PartnersSection } from "@/components/PartnersSection";
import RiskTriageQuiz from "@/components/RiskTriageQuiz";
import { REGULATORY_DOMAINS } from "@/data/domains";
import { usePageHead } from "@/hooks/usePageHead";
import { track } from "@/lib/analytics";

const features = [
  {
    icon: ListChecks,
    title: "Guided intake",
    body: "Plain-language prompts walk you through the details that matter, asset, transaction, parties, touchpoints, so nothing relevant slips by.",
  },
  {
    icon: Scale,
    title: "Every domain mapped",
    body: "Securities, AML, custody, tax, market integrity, data, governance: CLARITY maps your scenario across every relevant regulatory domain and organizes the findings by priority, so you know exactly where to focus next.",
  },
  {
    icon: FileText,
    title: "A memo you can hand off",
    body: "A polished memorandum, domain, headline, diligence questions, why each one matters, and priority, ready to share with your team, your client, or your file.",
  },
  {
    icon: Shield,
    title: "Made for working professionals",
    body: "Counsel, compliance and risk leaders, executives, and policymakers. Substance over jargon. Built so the tool meets you where you are.",
  },
];

const TRUST_BADGES = [
  { icon: Scale, label: "Attorney-designed", detail: "Structured issue-mapping methodology" },
  { icon: ListChecks, label: "Twelve-domain sweep", detail: "One pass, organized by priority" },
  { icon: ShieldCheck, label: "Memo-grade PDF", detail: "Branded, exportable, ready to share" },
];

/**
 * Rotating specimen snippets. Each is a real domain-shape the engine produces.
 * Cycling gives visitors a felt sense of breadth without more copy.
 */
const SPECIMENS = [
  {
    priority: "high",
    domain: "Securities",
    issue: "Whether the wrapped instrument retains the classification of its underlying reference asset across the bridge contract.",
    questions: [
      "Does the wrapper grant economic rights identical to the underlying?",
      "Does redemption mechanics shift the analysis under Howey?",
      "Are secondary venues making a market in the wrapped form?",
    ],
  },
  {
    priority: "high",
    domain: "AML / Sanctions",
    issue: "Whether the redemption path creates BSA obligations distinct from the broker-dealer affiliate's existing program.",
    questions: [
      "Does Fictional LLC independently qualify as an MSB under FinCEN guidance?",
      "How are OFAC screening obligations discharged for wallet-based counterparties?",
      "Are Travel Rule obligations triggered when proceeds settle to a non-U.S. VASP?",
    ],
  },
  {
    priority: "medium",
    domain: "Custody",
    issue: "Whether the collateral custody arrangement satisfies the qualified-custodian requirement for Fictional LLC's registered fund clients.",
    questions: [
      "Is the on-chain collateral held by a qualified custodian?",
      "Does the custodian's insurance policy extend to smart-contract exploit?",
      "How does the SEC's proposed Safeguarding Rule treat multisig collateral?",
    ],
  },
  {
    priority: "medium",
    domain: "Tax",
    issue: "Whether an in-kind redemption of the synthetic constitutes a taxable event, and how basis is reported.",
    questions: [
      "Does redemption to underlying reference assets trigger recognition under IRC §1001?",
      "How is basis tracked when the synthetic moves across wallets prior to redemption?",
      "What 1099-DA reporting posture applies to Fictional LLC versus the BD affiliate?",
    ],
  },
];

export default function Landing() {
  const loc = useLocation();
  const navigate = useNavigate();
  // /assessment is a shareable alias of the homepage that auto-opens the
  // CLARITY Assessment and carries its own share-card metadata.
  const isAssessment = loc.pathname === "/assessment";
  usePageHead(
    isAssessment
      ? {
          title: "The CLARITY Assessment™ | Four questions, your entry point, mapped",
          description: "A free three-minute assessment for digital assets counsel, GRC and policy professionals. Answer four questions about your seat and matters; get the right Clarity starting point instantly.",
          canonicalPath: "/assessment",
        }
      : {
          title: "Web3 Ready CLARITY™ | Digital Assets Risk, Made Clear",
          description: "Describe your situation in plain language. CLARITY maps your specific digital assets scenario across the legal, regulatory, and business domains that matter, organizes the findings by priority, and prepares you for the conversation with outside counsel.",
          canonicalPath: "/",
        },
  );
  const [specIdx, setSpecIdx] = useState(0);
  const [quizOpen, setQuizOpen] = useState(false);

  // Deep links: /assessment and /?assessment=1 open the CLARITY Assessment
  // (footer link, emails, shares). The query param is stripped after opening
  // so repeat clicks work; the /assessment path is kept as the canonical URL.
  useEffect(() => {
    if (isAssessment) {
      track("quiz_open", { source: "assessment_link" });
      setQuizOpen(true);
      return;
    }
    const params = new URLSearchParams(loc.search);
    if (params.get("assessment")) {
      track("quiz_open", { source: "deep_link" });
      setQuizOpen(true);
      params.delete("assessment");
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [loc.search, isAssessment, navigate]);
  useEffect(() => {
    const t = setInterval(() => setSpecIdx((i) => (i + 1) % SPECIMENS.length), 6500);
    return () => clearInterval(t);
  }, []);
  const spec = SPECIMENS[specIdx];
  return (
    <div data-testid="landing-page" className="public-copy">
      {/* HERO */}
      <section id="hero" className="border-b border-ink scroll-mt-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-0">
          {/* py trimmed from 36 to 24 at lg, and the supporting paragraph
              pulled up: the added promise line pushed the CTAs below the fold
              on a 900px-tall laptop, and the hero had the whitespace to give. */}
          <div className="lg:col-span-7 px-0 lg:pr-12 py-12 lg:py-14 border-r border-rule">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-ink/75 mb-5">
              Digital Assets Risk, Made Clear
            </div>
            {/* The same promise the Briefing page opens with, so both pages
                make one claim. It sits above the H1 rather than replacing it:
                the H1 carries the search intent. */}
            <p data-testid="hero-promise" className="font-serif text-base md:text-lg text-blueprint leading-snug mb-3">
              You are the one they turn to.
            </p>
            <h1 className="font-serif font-semibold text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-ink">
              See the legal and regulatory issues before they become business problems.
            </h1>
            <p data-testid="hero-subhead" className="mt-5 font-mono text-base text-ink/85 max-w-xl leading-relaxed">
              CLARITY is structured digital-asset regulatory issue intelligence for the lawyers,
              compliance leaders, advisers, and executives who do not specialize in crypto but are
              responsible for the decisions it touches. The free CLARITY Assessment shows you which
              regulatory domains your situation touches.
            </p>

            <div className="mt-5">
              <Link
                to="/assessment"
                data-testid="hero-cta-quiz"
                onClick={() => track("quiz_open", { source: "landing_hero" })}
                className="inline-flex items-center gap-2 bg-blueprint text-white px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] border border-blueprint hover:bg-ink hover:border-ink transition-colors"
              >
                Start Your Free CLARITY Assessment <ArrowRight size={16} />
              </Link>
              {/* Belongs to the assessment, so it sits directly under it. */}
              <div data-testid="hero-microcopy" className="mt-3 font-mono text-[11px] text-ink/70">
                Four questions. Three minutes. No account needed.
              </div>
            </div>

            {/* The lower-commitment second door, as ONE block: the session it
                registers you for and the action, inside a single border. A
                button above a card that said the same thing was two asks for
                one action. */}
            <NextSessionStrip
              source="landing-hero"
              className="mt-6 max-w-md"
              actionLabel="The CLARITY Briefing"
              actionTestId="hero-cta-briefing"
            />

            {/* Forbes credibility line, above the trust chips where visitors decide */}
            <ForbesHeroLine />

            {/* Credibility chips folded into the hero row, compact so they read as reinforcement rather than a full section */}
            <div data-testid="social-proof-bar" className="mt-4 flex flex-wrap gap-2">
              {TRUST_BADGES.map((b, i) => (
                <span
                  key={b.label}
                  data-testid={`trust-badge-${i}`}
                  className="inline-flex items-center gap-1.5 border border-ink/40 bg-white px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.22em] text-ink"
                >
                  <ShieldCheck size={11} strokeWidth={1.75} className="text-blueprint" />
                  {b.label}
                </span>
              ))}
            </div>

            {/* Mini-quote, professional social proof above the fold */}
            <figure
              data-testid="hero-mini-quote"
              className="mt-10 border-l-2 border-blueprint pl-4 max-w-xl"
            >
              <blockquote className="font-serif text-lg lg:text-xl leading-snug text-ink">
                &ldquo;It is not a generic checklist. It earns the trust of subject-matter experts.&rdquo;
              </blockquote>
              <figcaption className="font-mono text-xs text-ink/85 mt-2 leading-snug">
                Devon J. Euring, CAPFC &nbsp;·&nbsp; Founder &amp; CRO, Asterisk Defense &nbsp;·&nbsp; Beta Founder
              </figcaption>
            </figure>
          </div>

          <div className="lg:col-span-5 relative py-20 lg:py-36 px-0 lg:pl-12">
            <div data-testid="specimen-card" className="border border-ink p-0 bg-paper transition-opacity duration-300">
              <div className="border-b border-ink px-5 py-3 flex items-center justify-between">
                <span className="font-mono text-[11px] uppercase tracking-[0.25em]">Specimen Output Snippet</span>
                <span className="font-mono text-[11px] text-ink/70">/ priority: {spec.priority}</span>
              </div>
              <div key={specIdx} className="p-5 space-y-4">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink/70">Domain</div>
                  <div className="font-serif text-2xl tracking-tight">{spec.domain}</div>
                </div>
                <div className="border-t border-rule pt-4">
                  <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink/70 mb-2">Issue</div>
                  <div className="font-mono text-sm leading-relaxed">
                    {spec.issue}
                  </div>
                </div>
                <div className="border-t border-rule pt-4">
                  <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink/70 mb-2">Questions</div>
                  <ol className="font-mono text-sm leading-relaxed list-decimal list-outside pl-5 space-y-2">
                    {spec.questions.map((q) => <li key={q}>{q}</li>)}
                  </ol>
                </div>
              </div>
              <div className="border-t border-ink px-5 py-3 flex items-center justify-between gap-3">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink/70">
                  Mapped by domain. Organized by priority.
                </span>
                <div className="flex items-center gap-1.5" role="tablist" aria-label="Specimen output examples">
                  {SPECIMENS.map((spec, i) => (
                    <button
                      key={spec.domain}
                      role="tab"
                      aria-selected={i === specIdx}
                      aria-label={`Show ${SPECIMENS[i].domain} specimen`}
                      data-testid={`specimen-dot-${i}`}
                      onClick={() => setSpecIdx(i)}
                      className={`w-1.5 h-1.5 rounded-full transition-colors ${i === specIdx ? 'bg-blueprint' : 'bg-ink/25 hover:bg-ink/50'}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll cue */}
        <div className="flex justify-center pb-6 -mt-4">
          <a
            href="#method"
            data-testid="scroll-cue"
            aria-label="Scroll to learn more"
            className="inline-flex flex-col items-center gap-1 font-mono text-[11px] uppercase tracking-[0.3em] text-ink/70 hover:text-ink transition-colors"
          >
            <span>Scroll · Method &amp; reviews</span>
            <ChevronDown size={18} strokeWidth={1.5} className="animate-scrollHint" />
          </a>
        </div>
      </section>

      {/* CREDIBILITY BAR, authored-by strip anchored under the hero */}
      {/* Credentials in one line, directly under the hero CTAs. This
          replaces the taller "Authored by" grid that used to sit here. */}
      <AuthorityStrip />

      {/* What the free assessment can and cannot tell you, stated plainly, so
          the Index is never mistaken for the memorandum. */}
      <section id="what-four-questions" data-testid="what-four-questions" className="border-b border-ink bg-white scroll-mt-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12 lg:py-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <h2 className="lg:col-span-5 font-serif text-3xl sm:text-4xl tracking-tighter leading-[1.05] text-ink">
            What four questions can show you.
          </h2>
          <p className="lg:col-span-7 font-mono text-sm sm:text-base text-ink/85 leading-relaxed">
            Your Clarity Index: how many of twelve regulatory domains your situation touches, which
            ones, and the question worth asking in each. It won&rsquo;t tell you which regulators are in
            play or which issue ranks first. That takes your facts, and that&rsquo;s what the
            Issue-Mapping Memorandum is for.
          </p>
        </div>
      </section>

      {/* Kit and reviews moved up: proof of the product, then proof from
          people, before the longer explanatory sections. */}
      <ChecklistLeadBand onStartAssessment={() => setQuizOpen(true)} />

      <section id="reviews" data-testid="reviews-teaser" className="border-t border-ink bg-paper scroll-mt-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-24 lg:py-36 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-ink/75 mb-4">03 · Reviews</div>
            <ReviewHighlights />
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/reviews"
              data-testid="reviews-teaser-link"
              onClick={() => track("reviews_click", { source: "landing_teaser" })}
              className="inline-flex items-center gap-2 bg-white text-ink px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] border border-ink hover:bg-ink hover:text-white transition-colors"
            >
              Read the founder reviews <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* VISUAL PATH: Assessment -> Findings -> Activation */}
      <section id="how-it-works" data-testid="path-to-clarity" className="border-b border-ink bg-white scroll-mt-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-end mb-10 lg:mb-14">
            <div className="lg:col-span-8">
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-ink/75 mb-3">01 · The Path</div>
              <h2 className="font-serif text-4xl lg:text-5xl tracking-tighter font-medium leading-[1.0]">
                Your Path to Clarity.
              </h2>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <button
                type="button"
                data-testid="path-cta-assessment"
                onClick={() => { track("quiz_open", { source: "landing_path" }); setQuizOpen(true); }}
                className="inline-flex items-center gap-2 bg-ink text-white px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] border border-ink hover:bg-blueprint hover:border-blueprint transition-colors"
              >
                Start Your Free CLARITY Assessment <ArrowRight size={14} />
              </button>
            </div>
          </div>
          {/* Numbered stages with connectors: horizontal on desktop, vertical on mobile */}
          <ol data-testid="path-steps" className="grid grid-cols-1 md:grid-cols-3 gap-0">
            {[
              {
                n: "01",
                t: "Assessment",
                b: "Identify potential regulatory touchpoints. Four questions, three minutes, no account required.",
              },
              {
                n: "02",
                t: "Findings",
                b: "Understand your Clarity Index and the questions requiring examination.",
              },
              {
                n: "03",
                t: "Activation",
                b: "Put CLARITY to work on your own matter.",
              },
            ].map((s, i) => (
              <li key={s.n} data-testid={`path-step-${s.n}`} className="relative md:px-6 py-6 md:py-0 first:pt-0 md:first:pl-0 last:md:pr-0">
                <div className="flex items-center gap-4 md:block">
                  <div className="font-mono text-sm font-semibold text-white bg-blueprint w-10 h-10 flex items-center justify-center shrink-0 md:mb-5">{s.n}</div>
                  <div className="font-serif text-2xl tracking-tight leading-tight md:mt-0">{s.t}</div>
                </div>
                <div className="font-mono text-xs text-ink/85 mt-2 md:mt-3 leading-relaxed pl-14 md:pl-0">{s.b}</div>
                {/* Connector: horizontal arrow on desktop, vertical line on mobile */}
                {i < 2 && (
                  <>
                    <span aria-hidden="true" className="hidden md:flex absolute top-5 -right-[9px] z-10 w-[18px] h-[18px] items-center justify-center bg-white border border-ink rounded-full">
                      <ArrowRight size={10} strokeWidth={2} className="text-ink" />
                    </span>
                    <span aria-hidden="true" className="md:hidden absolute left-5 top-16 bottom-0 w-px bg-ink/25" />
                  </>
                )}
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FEATURES. (The public Briefing Desk news rail was removed by owner
          decision, Feb 2026; the member news feed on the dashboard remains.) */}
      <section id="method" className="scroll-mt-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-24 lg:py-40">
          <div>
            <div className="min-w-0">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 lg:mb-16">
            <div className="lg:col-span-4">
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-ink/75 mb-3">02 · Method</div>
              <h2 className="font-serif text-4xl lg:text-5xl tracking-tighter font-medium leading-[1.0]">
                Guided intake.<br/>Formal output.<br/>No chat.
              </h2>
            </div>
            <div className="lg:col-span-8 lg:pt-2">
              <ol data-testid="method-steps" className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {[
                  { n: "01", t: "Describe the scenario", b: "Plain language. No tech vocabulary required." },
                  { n: "02", t: "We map what matters", b: "A domain-by-domain map of your exposures, organized by priority." },
                  { n: "03", t: "Take the next step", b: "A polished memorandum for client, team, or file." },
                ].map((s) => (
                  <li key={s.n} data-testid={`method-step-${s.n}`} className="border-l border-ink pl-4">
                    <div className="font-serif text-5xl font-light tracking-tighter leading-none text-blueprint">{s.n}</div>
                    <div className="font-serif text-xl mt-3 tracking-tight leading-tight">{s.t}</div>
                    <div className="font-mono text-xs text-ink/85 mt-2 leading-relaxed">{s.b}</div>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Regulatory-domain chip grid: the breadth of one sweep, at a glance */}
          <div data-testid="domain-chip-grid" className="mb-14 lg:mb-16">
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink/75 mb-3">
              One sweep across twelve regulatory domains
            </div>
            <div className="flex flex-wrap gap-2">
              {REGULATORY_DOMAINS.map((d) => (
                <span
                  key={d}
                  className="inline-flex items-center border border-ink/40 bg-white px-3 py-1.5 font-mono text-xs text-ink hover:border-ink transition-colors"
                >
                  {d}
                </span>
              ))}
            </div>
          </div>

          {/* Pricing ladder infographic, pre-frames the tiers and routes to /pricing */}
          <div id="pricing" className="scroll-mt-16">
            <LadderAtAGlance />
            {/* Price lives on /pricing now. This is the one pointer to it. */}
            <div data-testid="ladder-pricing-link" className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-ink/70">
              <Link to="/pricing" className="text-blueprint underline underline-offset-2 hover:text-ink transition-colors">
                See plans and pricing &rarr;
              </Link>
              <span className="text-ink/35 mx-2">&middot;</span>
              2026 founding rates end December 31.
            </div>
            {/* Social proof at the buying decision: an approved beta review that
                speaks to practical usefulness, placed beside the tiers. */}
            <figure data-testid="tier-testimonial" className="mt-10 border-l-2 border-blueprint pl-5 max-w-2xl">
              <blockquote className="font-serif text-lg lg:text-xl leading-snug text-ink">
                &ldquo;I built a scenario specifically designed to stress-test the tool across its hardest dimensions. It didn&rsquo;t fold. The output held up under expert scrutiny.&rdquo;
              </blockquote>
              <figcaption className="font-mono text-xs text-ink/85 mt-2 leading-snug">
                Devon J. Euring, CAPFC &nbsp;&middot;&nbsp; Founder and Chief Risk Officer, Asterisk Defense &nbsp;&middot;&nbsp; Beta Founder
              </figcaption>
            </figure>
          </div>

          <div id="who-its-for" className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-rule scroll-mt-16">
            {features.map((f) => (
              <div
                key={f.title}
                data-testid={`feature-${f.title.toLowerCase().replace(/\s/g, '-')}`}
                className="border-r border-b border-rule p-8 hover:bg-paper transition-colors"
              >
                <f.icon size={22} strokeWidth={1.25} className="text-blueprint mb-4" />
                <div className="font-serif text-2xl tracking-tight mb-2">{f.title}</div>
                <div className="font-mono text-sm text-ink/85 leading-relaxed">{f.body}</div>
              </div>
            ))}
          </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROOF: a scrollable excerpt of the real sample memorandum, placed
          where the walkthrough video used to sit, so visitors see the actual
          output before they are asked to pay. */}
      <section id="proof" data-testid="landing-proof" className="border-t border-ink bg-paper scroll-mt-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-24 lg:pt-36 pb-8 lg:pb-16">
          <MemoProofSection
            source="landing"
            heading="What an Issue-Mapping Memorandum Looks Like."
            supportingCopy="Explore a sample CLARITY memorandum generated from a fictional digital-asset scenario and subsequently reviewed for legal accuracy. See how the report identifies factual gaps, organizes regulatory questions, and prepares professionals for further analysis."
            sampleLabel="A Real CLARITY Output. Fictional Scenario. Human-Vetted Sample."
            excerptLabel="Issue-Mapping Memorandum · Sample · Fictional scenario"
            ctaLabel="Get the full sample memorandum"
            ctaNote="Sent as a PDF with the Clarity Issue-Mapping Starter Kit."
          />
        </div>
      </section>

      {/* THE CATEGORY, § 02a: competitive differentiation by contrast.
          Category language only, no competitor names, copy is verbatim. */}
      <section id="comparison" data-testid="landing-category" className="border-t border-ink bg-white scroll-mt-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-24 lg:py-36">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-ink/75 mb-3">02a · The Category</div>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight font-semibold leading-tight max-w-3xl">
            Not research. Not a feed. Not a chatbot. Regulatory intelligence.
          </h2>
          <p className="font-mono text-sm text-ink/80 leading-relaxed mt-4 max-w-2xl">
            Professionals evaluating CLARITY often compare it to tools built for different jobs. Here is the distinction that matters.
          </p>

          <div data-testid="category-cards" className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
            {[
              {
                label: "General legal AI platforms",
                body: "Built to research and draft across every practice area. CLARITY does one thing those platforms do not: it takes your specific digital asset scenario through a structured intake and returns a prioritized map of the regulatory issues it triggers, across twelve domains, before any drafting begins.",
              },
              {
                label: "Regulatory intelligence feeds",
                body: "Horizon-scanning tools tell you what rules changed this week. CLARITY tells you which issues your specific matter raises. A feed is a firehose. CLARITY is a targeted memo, organized by domain and priority, ready for your file.",
              },
              {
                label: "Blockchain analytics platforms",
                body: "Transaction monitoring and wallet screening review activity after it happens. CLARITY maps regulatory exposure before you act: the questions to resolve while the structure, the product, or the deal is still on the whiteboard.",
              },
              {
                label: "General-purpose chatbots",
                body: "A blank prompt returns an improvised answer. CLARITY runs a guided intake built by a recognized digital assets law authority, sweeps twelve regulatory domains in one pass, and returns a structured, memo-grade PDF organized by domain and priority. No freeform prompting, and no chat.",
              },
            ].map((card, i) => (
              <div key={card.label} data-testid={`category-card-${i + 1}`} className="border-2 border-ink bg-white">
                <div className="flex items-center justify-between gap-3 border-b border-ink px-5 py-3">
                  <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-blueprint">{card.label}</span>
                  <span className="font-mono text-[10px] tracking-[0.2em] text-ink/40 shrink-0">0{i + 1} / 04</span>
                </div>
                <p className="px-5 py-4 font-mono text-sm leading-relaxed text-ink/85">{card.body}</p>
              </div>
            ))}
          </div>

          <div data-testid="category-closing" className="mt-12 border-l-4 border-blueprint pl-6 sm:pl-8 max-w-3xl">
            <h3 className="font-serif text-xl sm:text-2xl tracking-tight font-semibold leading-tight">
              A structured issue map before the first billable hour.
            </h3>
            <p className="font-mono text-sm text-ink/85 leading-relaxed mt-3">
              CLARITY runs your specific scenario through a structured, multi-domain intake: it identifies the regulatory issues your facts raise and organizes them by priority, so you walk into the conversation with counsel already knowing where to focus. CLARITY identifies issues for examination. It does not provide legal conclusions, and your counsel stays the decision-maker. Equipped, not exposed.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <button
                type="button"
                data-testid="category-cta-assessment"
                onClick={() => { track("quiz_open", { source: "landing_category" }); setQuizOpen(true); }}
                className="inline-flex items-center gap-2 bg-blueprint text-white px-5 py-3 font-mono text-xs uppercase tracking-[0.18em] border border-blueprint hover:bg-ink hover:border-ink transition-colors"
              >
                Start the CLARITY Assessment <ArrowRight size={14} />
              </button>
              <Link
                to="/pricing"
                data-testid="category-link-pricing"
                className="font-mono text-xs uppercase tracking-[0.18em] text-ink underline underline-offset-4 hover:text-blueprint"
              >
                Compare tiers
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* EDUCATIONAL ALTERNATIVES: free paths for visitors not ready to buy */}
      <section id="resources" data-testid="educational-alternatives" className="border-t border-ink bg-paper scroll-mt-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-24 lg:py-32">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-ink/75 mb-3">03a · Learn first</div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium leading-[1.0] max-w-2xl">
            Not ready to run a scenario? Start with the free resources.
          </h2>
          <p className="font-mono text-sm text-ink/80 leading-relaxed mt-4 max-w-2xl">
            Two ways to get value from CLARITY before you buy anything. Both are free, and neither requires an account.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
            <div data-testid="resource-starter-kit" className="border-2 border-ink bg-white p-6 flex flex-col">
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-blueprint mb-2">The Starter Kit</div>
              <h3 className="font-serif text-2xl tracking-tight">Clarity Issue-Mapping Starter Kit</h3>
              <p className="font-mono text-sm text-ink/85 leading-relaxed mt-3 flex-1">
                The Digital Assets Regulatory Issue-Spotting Checklist and the full human-vetted sample memorandum, sent as PDFs you can keep and share.
              </p>
              <Link
                to="/checklist"
                data-testid="resource-starter-kit-cta"
                onClick={() => track("checklist_click", { source: "landing_resources" })}
                className="mt-6 inline-flex items-center gap-2 bg-ink text-white px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] border border-ink hover:bg-blueprint hover:border-blueprint transition-colors self-start"
              >
                Get the Starter Kit <ArrowRight size={14} />
              </Link>
            </div>
            <div data-testid="resource-briefing" className="border-2 border-ink bg-white p-6 flex flex-col">
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-blueprint mb-2">The CLARITY Briefing</div>
              <h3 className="font-serif text-2xl tracking-tight">Live sessions and free replays</h3>
              <p className="font-mono text-sm text-ink/85 leading-relaxed mt-3 flex-1">
                A weekly working session on the digital-asset rules that actually move. Register for the next live briefing or watch the available replays.
              </p>
              <NextSessionStrip source="landing-resources" className="mt-5" actionLabel="Register for the next briefing" actionTestId="resource-briefing-cta" />
              <Link
                to="/webinar"
                data-testid="resource-briefing-replays"
                onClick={() => track("webinar_cta_click", { source: "landing_resources_replays" })}
                className="mt-3 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-ink underline underline-offset-4 hover:text-blueprint"
              >
                See sessions and free replays <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Partners (hidden entirely when none qualify for public display) */}
      <PartnersSection />

      {/* CTA */}
      <section id="apply" className="border-t border-ink bg-ink text-white scroll-mt-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/70 mb-3">04 · Get started</div>
            <h2 className="font-serif text-4xl lg:text-6xl tracking-tighter font-medium leading-[1.0]">
              Start Where You Are.<br/>Build The Clarity You Need.
            </h2>
            <div className="mt-6 font-mono text-sm text-white/80 max-w-xl leading-relaxed">
              Most people start with the free assessment and the sample memorandum, then run their own matter when the timing is right. Wherever you are in that arc, there is a next step here.
            </div>
            <img
              src="/brand/clarity-blur-navy.gif"
              alt="Web3 Ready CLARITY compass mark resolving from blur to clarity"
              data-testid="landing-cta-logo"
              className="mt-10 h-16 sm:h-20 w-auto"
              loading="lazy"
            />
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <button
              type="button"
              data-testid="cta-bottom-assessment"
              onClick={() => { track("quiz_open", { source: "landing_bottom" }); setQuizOpen(true); }}
              className="block w-full text-center bg-white text-ink px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] border border-white hover:bg-blueprint hover:text-white hover:border-blueprint transition-colors"
            >
              Start Your Free CLARITY Assessment →
            </button>
            <Link
              to="/pricing"
              data-testid="cta-bottom-pricing"
              onClick={() => track("pricing_view", { source: "landing_bottom" })}
              className="block text-center bg-transparent text-white px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] border border-white/40 hover:border-white transition-colors"
            >
              Explore Pricing
            </Link>
            <Link
              to="/webinar"
              data-testid="cta-bottom-briefing"
              onClick={() => track("webinar_cta_click", { source: "landing_bottom" })}
              className="block text-center bg-transparent text-white px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] border border-white/40 hover:border-white transition-colors"
            >
              Watch a Free Briefing
            </Link>
            <Link
              to="/login"
              data-testid="cta-bottom-signin"
              onClick={() => track("signed_in", { source: "landing_bottom" })}
              className="block text-center font-mono text-[11px] uppercase tracking-[0.2em] text-white/70 hover:text-white underline underline-offset-4 pt-2"
            >
              Already a member? Sign in
            </Link>
          </div>
        </div>
      </section>

      {/* The CLARITY Assessment modal, opened from the hero and sticky CTAs */}
      <RiskTriageQuiz open={quizOpen} onOpenChange={setQuizOpen} />
    </div>
  );
}
